import React, { useCallback, useEffect, useMemo, useState } from "react";
import { authFetch } from "../api";

/**
 * SavedSearchBar — name the current filter set, reload it later, and optionally get emailed
 * when its answer changes.
 *
 * Saved searches are PRIVATE to the logged-in user and are never auto-loaded: the board opens
 * clean and a search is applied only when picked from the dropdown.
 *
 * Contract:
 *   scope           which board these belong to (server-side whitelist; 'sale_reviews' today)
 *   currentFilters  the live filter draft (the page's collectFilters() object)
 *   onLoad(filters) called with a saved search's filters — the page sets its inputs and searches
 *
 * The "modified" marker compares the draft against the selected search through the same
 * normalisation the server applies on save (blanks dropped, numerics as numbers), so a value
 * retyped identically doesn't read as a change.
 */

const NUMERIC_KEYS = new Set([
  "beds_min", "beds_max", "baths_min", "baths_max", "reno_min", "reno_max",
  "sqft_min", "sqft_max", "price_min", "price_max", "cap_rate_min", "cap_rate_max",
  "dom_max", "carrying_max",
]);

const CADENCES = [
  { value: "daily", label: "Daily" },
  { value: "twice_weekly", label: "Twice a week (Mon & Thu)" },
  { value: "weekly", label: "Weekly (Mon)" },
];
const CADENCE_LABEL = { daily: "daily", twice_weekly: "2×/week", weekly: "weekly" };

function normalizeFilters(f) {
  const out = {};
  Object.keys(f || {}).sort().forEach((k) => {
    let v = f[k];
    if (v === "" || v === null || v === undefined) return;
    if (Array.isArray(v)) {
      if (v.length === 0) return;
      v = [...v].sort((a, b) => a - b);
    } else if (NUMERIC_KEYS.has(k)) {
      const num = Number(v);
      if (Number.isNaN(num)) return;
      v = num;
    }
    out[k] = v;
  });
  return out;
}

const fingerprint = (f) => JSON.stringify(normalizeFilters(f));

// authFetch resolves with the parsed body whatever the status, so an API error arrives as a
// `detail` field rather than a rejection.
function errorOf(res) {
  if (!res || !res.detail) return null;
  return typeof res.detail === "string" ? res.detail : JSON.stringify(res.detail);
}

export default function SavedSearchBar({ scope = "sale_reviews", currentFilters, onLoad }) {
  const [searches, setSearches] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [busy, setBusy] = useState(false);
  // { mode: 'create' | 'edit', name, notifyEnabled, notifyFrequency }
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await authFetch(`/saved-searches?scope=${encodeURIComponent(scope)}`);
      setSearches(data.saved_searches || []);
    } catch (e) {
      console.error("loading saved searches failed:", e);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const selected = useMemo(
    () => searches.find((s) => String(s.id) === String(selectedId)) || null,
    [searches, selectedId]
  );

  const isModified = useMemo(
    () => !!selected && fingerprint(selected.filters) !== fingerprint(currentFilters),
    [selected, currentFilters]
  );

  const handleSelect = async (e) => {
    const id = e.target.value;
    setSelectedId(id);
    if (!id) return;
    const search = searches.find((s) => String(s.id) === String(id));
    if (!search) return;
    onLoad(search.filters || {});
    // last_used_at orders the dropdown; a failure here must not block the load.
    authFetch(`/saved-searches/${id}/used`, { method: "POST" }).then(load).catch(() => {});
  };

  const openCreate = () => setDialog({
    mode: "create",
    name: selected ? `${selected.name} copy` : "",
    notifyEnabled: false,
    notifyFrequency: "daily",
  });

  const openEdit = () => selected && setDialog({
    mode: "edit",
    name: selected.name,
    notifyEnabled: !!selected.notify_enabled,
    notifyFrequency: selected.notify_frequency || "daily",
  });

  const submitDialog = async () => {
    const name = (dialog.name || "").trim();
    if (!name) { window.alert("Please give the search a name."); return; }
    setBusy(true);
    try {
      let res;
      if (dialog.mode === "create") {
        const body = {
          name, scope,
          filters: normalizeFilters(currentFilters),
          notify_enabled: dialog.notifyEnabled,
          notify_frequency: dialog.notifyFrequency,
        };
        res = await authFetch("/saved-searches", { method: "POST", body: JSON.stringify(body) });
        let err = errorOf(res);
        if (err && /already have a saved search named/i.test(err)) {
          if (!window.confirm(`"${name}" already exists. Replace its filters?`)) return;
          res = await authFetch("/saved-searches", {
            method: "POST", body: JSON.stringify({ ...body, overwrite: true }),
          });
          err = errorOf(res);
        }
        if (err) { window.alert(`Could not save: ${err}`); return; }
      } else {
        res = await authFetch(`/saved-searches/${selected.id}`, {
          method: "PUT",
          body: JSON.stringify({
            name,
            notify_enabled: dialog.notifyEnabled,
            notify_frequency: dialog.notifyFrequency,
          }),
        });
        const err = errorOf(res);
        if (err) { window.alert(`Could not save: ${err}`); return; }
      }
      await load();
      if (res.saved_search) setSelectedId(String(res.saved_search.id));
      setDialog(null);
    } finally {
      setBusy(false);
    }
  };

  const handleUpdate = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await authFetch(`/saved-searches/${selected.id}`, {
        method: "PUT",
        body: JSON.stringify({ filters: normalizeFilters(currentFilters) }),
      });
      const err = errorOf(res);
      if (err) { window.alert(`Could not update: ${err}`); return; }
      await load();
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete saved search "${selected.name}"? The filters on screen stay as they are.`)) return;
    setBusy(true);
    try {
      const res = await authFetch(`/saved-searches/${selected.id}`, { method: "DELETE" });
      const err = errorOf(res);
      if (err) { window.alert(`Could not delete: ${err}`); return; }
      setSelectedId("");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const btn = (extra = {}) => ({
    padding: "6px 12px", fontSize: "13px", fontWeight: 500, borderRadius: "4px",
    border: "1px solid #ced4da", background: "#fff", color: "#212529",
    cursor: busy ? "wait" : "pointer", whiteSpace: "nowrap", ...extra,
  });
  const disabledBtn = { opacity: 0.5, cursor: "not-allowed" };
  const inputStyle = {
    width: "100%", padding: "8px", border: "1px solid #ced4da", borderRadius: "4px",
    fontSize: "14px", boxSizing: "border-box",
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "12px", paddingBottom: "12px", borderBottom: "1px solid #eee" }}>
      <label style={{ fontSize: "12px", fontWeight: 600, color: "#495057" }}>Saved search</label>

      <select value={selectedId} onChange={handleSelect} disabled={busy}
        style={{ padding: "6px 8px", border: "1px solid #ced4da", borderRadius: "4px", fontSize: "13px", background: "#fff", minWidth: "220px" }}>
        <option value="">— none —</option>
        {searches.map((s) => (
          <option key={s.id} value={s.id}>
            {s.notify_enabled ? "🔔 " : ""}{s.name}
          </option>
        ))}
      </select>

      {selected?.notify_enabled && (
        <span title={`You get an email when this search changes (${CADENCE_LABEL[selected.notify_frequency] || "daily"})`}
          style={{ fontSize: "11px", fontWeight: 600, color: "#1565c0", background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: "10px", padding: "2px 8px" }}>
          🔔 {CADENCE_LABEL[selected.notify_frequency] || "daily"}
        </span>
      )}

      {isModified && (
        <span title="The filters on screen differ from the saved ones. Use Update to overwrite, or Save as… for a new search."
          style={{ fontSize: "11px", fontWeight: 600, color: "#b26a00", background: "#fff8e1", border: "1px solid #ffe0b2", borderRadius: "10px", padding: "2px 8px" }}>
          modified
        </span>
      )}

      <button onClick={openCreate} disabled={busy} style={btn()}>Save as…</button>
      <button onClick={handleUpdate} disabled={busy || !selected || !isModified}
        title={selected ? "Overwrite this saved search with the filters on screen" : "Pick a saved search first"}
        style={btn(!selected || !isModified ? disabledBtn : {})}>
        Update filters
      </button>
      <button onClick={openEdit} disabled={busy || !selected}
        title="Rename, or turn email alerts on or off"
        style={btn(!selected ? disabledBtn : {})}>
        Rename / alerts…
      </button>
      <button onClick={handleDelete} disabled={busy || !selected}
        style={btn(!selected ? { ...disabledBtn, color: "#c62828", borderColor: "#ef9a9a" } : { color: "#c62828", borderColor: "#ef9a9a" })}>
        Delete
      </button>

      {dialog && (
        <div onClick={() => !busy && setDialog(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: "8px", padding: "22px", width: "440px", maxWidth: "92vw", boxShadow: "0 8px 30px rgba(0,0,0,0.25)" }}>
            <h3 style={{ margin: "0 0 16px", fontSize: "17px" }}>
              {dialog.mode === "create" ? "Save this search" : "Rename / alerts"}
            </h3>

            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#495057", marginBottom: "4px" }}>
              Name
            </label>
            <input autoFocus value={dialog.name} style={inputStyle}
              onChange={(e) => setDialog({ ...dialog, name: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") submitDialog(); }}
              placeholder="e.g. 1BR Williamsburg under $1M" />

            <div style={{ marginTop: "18px", padding: "12px", background: "#f8f9fa", borderRadius: "6px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}>
                <input type="checkbox" checked={dialog.notifyEnabled}
                  onChange={(e) => setDialog({ ...dialog, notifyEnabled: e.target.checked })} />
                Email me when this search changes
              </label>
              <div style={{ fontSize: "12px", color: "#666", margin: "6px 0 0 24px" }}>
                New matches, price changes and status changes.
              </div>

              {dialog.notifyEnabled && (
                <div style={{ margin: "12px 0 0 24px" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#495057", marginBottom: "4px" }}>
                    How often at most
                  </label>
                  <select value={dialog.notifyFrequency} style={{ ...inputStyle, width: "auto", background: "#fff" }}
                    onChange={(e) => setDialog({ ...dialog, notifyFrequency: e.target.value })}>
                    {CADENCES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                  {/* Sets the expectation that enabling records a baseline rather than replaying
                      the whole current match set, and that quiet periods mean no email. */}
                  <div style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
                    You'll hear about changes from now on — not the listings already matching
                    today. Nothing is sent when nothing changed.
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "20px" }}>
              <button onClick={() => setDialog(null)} disabled={busy} style={btn()}>Cancel</button>
              <button onClick={submitDialog} disabled={busy}
                style={btn({ background: "#000", color: "#fff", borderColor: "#000", fontWeight: 600 })}>
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
